import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import config from "../Config";
import Topbar from "../Components/Topbar";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  listSections,
  createSection,
  updateSection,
  deleteSection,
} from "../Config/serviceContentSectionService";

const SECTION_TYPES = ["TEXT", "IMAGE", "VIDEO"];
const IMAGE_UPLOAD_URL = "https://hspbreguarptwwdfhtuj.supabase.co/functions/v1/image-upload";

function ServiceContentSections() {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploadingImageIndex, setUploadingImageIndex] = useState(null);
  const [formData, setFormData] = useState({
    service_id: "",
    header: "",
    sub_header: "",
    section_type: "TEXT",
    content: { blocks: [] },
    background: "#FFFFFF",
    display_order: 0,
  });

  const fetchService = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${config.apiUrl}/api/v1/admin/service/${serviceId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setService(data);
      } else {
        throw new Error("Failed to fetch service");
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [serviceId]);

  const fetchSections = useCallback(async () => {
    try {
      const res = await listSections({ service_id: serviceId });
      const data = Array.isArray(res) ? res : res?.data ?? [];
      setSections(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.message || "Failed to fetch sections");
      setSections([]);
    }
  }, [serviceId]);

  useEffect(() => {
    if (serviceId) {
      fetchService();
      fetchSections();
    }
  }, [serviceId, fetchService, fetchSections]);

  const resetForm = () => {
    setFormData({
      service_id: serviceId || "",
      header: "",
      sub_header: "",
      section_type: "TEXT",
      content: { blocks: [] },
      background: "#FFFFFF",
      display_order: sections.length,
    });
    setEditingId(null);
  };

  const openAdd = () => {
    resetForm();
    setFormData((prev) => ({
      ...prev,
      service_id: serviceId,
      display_order: sections.length,
    }));
    setShowModal(true);
  };

  const openEdit = (section) => {
    const c = section.content;
    const isObj = typeof c === "object" && c !== null;
    let content;
    if (section.section_type === "IMAGE") {
      const urls = isObj && Array.isArray(c.urls) ? c.urls : [];
      const fromImages = isObj && Array.isArray(c.images) ? c.images.map((i) => (i && i.url) || i) : [];
      content = { urls: urls.length > 0 ? urls : fromImages };
    } else if (section.section_type === "VIDEO") {
      const urls = isObj && Array.isArray(c.urls) ? c.urls : [];
      const fromVideos = isObj && Array.isArray(c.videos) ? (c.videos.map((v) => (v && v.url) || v)) : [];
      content = { urls: urls.length > 0 ? urls : fromVideos };
    } else {
      content = isObj ? { ...c } : { blocks: [String(c)] };
      if (!Array.isArray(content.blocks)) content.blocks = [];
    }
    setEditingId(section.id);
    setFormData({
      service_id: section.service_id,
      header: section.header || "",
      sub_header: section.sub_header || "",
      section_type: section.section_type || "TEXT",
      content,
      background: section.background || "#FFFFFF",
      display_order: section.display_order ?? 0,
    });
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const setContentBlocks = (blocks) => {
    setFormData((prev) => ({ ...prev, content: { ...prev.content, blocks } }));
  };

  const setContentUrls = (urls) => {
    setFormData((prev) => ({ ...prev, content: { ...prev.content, urls } }));
  };

  const getContentUrls = () => {
    const urls = formData.content?.urls;
    return Array.isArray(urls) ? urls : [];
  };

  const getContentVideoUrls = () => {
    const urls = formData.content?.urls;
    return Array.isArray(urls) ? urls : [];
  };

  const getContentBlocks = () => {
    const blocks = formData.content?.blocks;
    return Array.isArray(blocks) ? blocks : [];
  };

  const addContentUrl = (url) => {
    const urls = [...getContentUrls(), url];
    setContentUrls(urls);
  };

  const removeContentUrl = (index) => {
    const urls = getContentUrls().filter((_, i) => i !== index);
    setContentUrls(urls);
  };

  const setContentUrlAt = (index, url) => {
    const urls = [...getContentUrls()];
    urls[index] = url;
    setContentUrls(urls);
  };

  const handleImageUpload = async (file, index) => {
    setUploadingImageIndex(index);
    try {
      const token = localStorage.getItem("token");
      const form = new FormData();
      form.append("file", file);
      const response = await fetch(IMAGE_UPLOAD_URL, {
        method: "POST",
        headers: { "X-Client": "ADMIN", Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!response.ok) throw new Error("Upload failed");
      const data = await response.json();
      const url = Array.isArray(data) ? data[0] : data?.url;
      if (url) {
        if (index === -1) addContentUrl(url);
        else setContentUrlAt(index, url);
        toast.success("Image uploaded");
      } else {
        throw new Error("No URL returned");
      }
    } catch (e) {
      toast.error(e.message || "Failed to upload image");
    } finally {
      setUploadingImageIndex(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let content;
    if (formData.section_type === "TEXT") {
      content = { blocks: getContentBlocks().map((s) => s.trim()).filter(Boolean) };
    } else if (formData.section_type === "IMAGE") {
      content = { urls: getContentUrls().map((s) => s.trim()).filter(Boolean), blocks: [] };
    } else {
      content = { urls: getContentVideoUrls().map((s) => s.trim()).filter(Boolean) };
    }
    const payload = {
      service_id: formData.service_id,
      header: formData.header || null,
      sub_header: formData.sub_header || null,
      section_type: formData.section_type,
      content,
      background: formData.background || null,
      display_order: Number(formData.display_order) || 0,
    };
    try {
      let res;
      if (editingId) {
        res = await updateSection(editingId, payload);
      } else {
        res = await createSection(payload);
      }
      if (res?.id || res?.data?.id || (res?.success !== false && !res?.message)) {
        toast.success(editingId ? "Section updated" : "Section created");
        setShowModal(false);
        resetForm();
        fetchSections();
      } else {
        throw new Error(res?.message || "Request failed");
      }
    } catch (e) {
      toast.error(e.message || "Failed to save");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this section? (soft delete)")) return;
    try {
      const res = await deleteSection(id);
      if (res?.success !== false && res?.message !== "Failed") {
        toast.success("Section deleted");
        fetchSections();
      } else {
        throw new Error(res?.message || "Delete failed");
      }
    } catch (e) {
      toast.error(e.message || "Failed to delete");
    }
  };

  const getSectionContentBlocks = (sec) => {
    const c = sec.content;
    if (!c || typeof c !== "object") return [];
    const blocks = c.blocks;
    return Array.isArray(blocks) ? blocks : [];
  };

  /** Image URLs: content.urls (array of strings) or content.images[].url */
  const getSectionImageUrls = (sec) => {
    const c = sec.content;
    if (!c || typeof c !== "object") return [];
    if (Array.isArray(c.urls) && c.urls.length > 0) {
      return c.urls.filter(Boolean);
    }
    const images = c.images;
    if (!Array.isArray(images)) return [];
    return images.map((i) => (i && i.url) || i).filter(Boolean);
  };

  /** Video URLs from API format: content.videos[].url or content.urls */
  const getSectionVideoUrls = (sec) => {
    const c = sec.content;
    if (!c || typeof c !== "object") return [];
    const videos = c.videos;
    if (Array.isArray(videos) && videos.length > 0) {
      return videos.map((v) => (v && v.url) || v).filter(Boolean);
    }
    const urls = c.urls;
    return Array.isArray(urls) ? urls : [];
  };

  if (loading || !service) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Topbar />
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/services")}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              ← Services
            </button>
            <button
              onClick={() => navigate(`/services/view/${serviceId}`)}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              View service
            </button>
            <h1 className="text-2xl font-bold text-gray-900">
              Content sections — {service.title || serviceId}
            </h1>
          </div>
          <button
            onClick={openAdd}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            Add section
          </button>
        </div>

        <p className="text-sm text-gray-500 mb-4">
          These sections are shown to users after they book this service.
        </p>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          {sections.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No content sections yet. Add one to show after booking.
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {sections.map((sec) => (
                <div
                  key={sec.id}
                  className="p-5 hover:bg-gray-50/50 transition-colors"
                  style={
                    sec.background
                      ? { backgroundColor: `${sec.background}15` }
                      : undefined
                  }
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          #{sec.display_order}
                        </span>
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-800">
                          {sec.section_type}
                        </span>
                        {sec.background && (
                          <span
                            className="inline-block w-5 h-5 rounded border border-gray-300 flex-shrink-0"
                            style={{ backgroundColor: sec.background }}
                            title={sec.background}
                          />
                        )}
                      </div>
                      {sec.header && (
                        <h3 className="text-lg font-semibold text-gray-900 mb-0.5">
                          {sec.header}
                        </h3>
                      )}
                      {sec.sub_header && (
                        <p className="text-sm text-gray-600 mb-3">{sec.sub_header}</p>
                      )}

                      {/* Content preview */}
                      <div className="mt-3 rounded-md border border-gray-200 bg-gray-50/80 p-4">
                        {sec.section_type === "TEXT" && (
                          <div className="text-sm text-gray-700 space-y-1">
                            {getSectionContentBlocks(sec).length > 0 ? (
                              getSectionContentBlocks(sec).map((block, i) => (
                                <p key={i} className="leading-relaxed">
                                  {block}
                                </p>
                              ))
                            ) : (
                              <span className="text-gray-400 italic">No text content</span>
                            )}
                          </div>
                        )}
                        {sec.section_type === "IMAGE" && (
                          <div className="flex flex-wrap gap-3">
                            {getSectionImageUrls(sec).length > 0 ? (
                              getSectionImageUrls(sec).map((url, i) => (
                                <a
                                  key={i}
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block flex-shrink-0 rounded-lg border border-gray-200 overflow-hidden bg-gray-100 hover:border-indigo-400 transition-colors"
                                >
                                  <img
                                    src={url}
                                    alt=""
                                    className="h-24 w-24 object-cover"
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='96' height='96' viewBox='0 0 96 96'%3E%3Crect fill='%23e5e7eb' width='96' height='96'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%239ca3af' font-size='12' font-family='sans-serif'%3EImage%3C/text%3E%3C/svg%3E";
                                    }}
                                  />
                                </a>
                              ))
                            ) : (
                              <span className="text-sm text-gray-400 italic">No images</span>
                            )}
                          </div>
                        )}
                        {sec.section_type === "VIDEO" && (
                          <div className="text-sm text-gray-700 space-y-1">
                            {getSectionVideoUrls(sec).length > 0 ? (
                              getSectionVideoUrls(sec).map((url, i) => (
                                <a
                                  key={i}
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block text-indigo-600 hover:text-indigo-800 truncate max-w-md"
                                >
                                  Video {i + 1}: {url}
                                </a>
                              ))
                            ) : (
                              <span className="text-gray-400 italic">No video URLs</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => openEdit(sec)}
                        className="px-3 py-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-md"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(sec.id)}
                        className="px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-[120] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
            <div
              className="fixed inset-0 z-0 bg-gray-500 bg-opacity-75"
              aria-hidden="true"
            />
            <div
              className="relative z-10 inline-block bg-white rounded-lg shadow-xl text-left overflow-hidden align-middle sm:max-w-xl w-full"
              role="dialog"
              aria-modal="true"
            >
              <div className="px-6 py-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {editingId ? "Edit section" : "Add section"}
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Service</label>
                    <input
                      type="text"
                      value={formData.service_id}
                      readOnly
                      className="mt-1 block w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Header</label>
                    <input
                      type="text"
                      name="header"
                      value={formData.header}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Sub header</label>
                    <input
                      type="text"
                      name="sub_header"
                      value={formData.sub_header}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Section type</label>
                    <select
                      name="section_type"
                      value={formData.section_type}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    >
                      {SECTION_TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>

                  {formData.section_type === "TEXT" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Content (one block per line)</label>
                      <textarea
                        value={getContentBlocks().join("\n")}
                        onChange={(e) =>
                          setContentBlocks(e.target.value.split("\n"))
                        }
                        rows={4}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                        placeholder="Line 1&#10;Line 2"
                      />
                    </div>
                  )}

                  {formData.section_type === "IMAGE" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Images</label>
                      <div className="space-y-2">
                        {getContentUrls().map((url, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <img src={url} alt="" className="h-12 w-12 object-cover rounded" />
                            <input
                              type="text"
                              value={url}
                              onChange={(e) => setContentUrlAt(idx, e.target.value)}
                              className="flex-1 rounded-md border border-gray-300 px-2 py-1 text-sm"
                            />
                            <label className="cursor-pointer">
                              <span className="px-2 py-1 bg-gray-200 rounded text-sm hover:bg-gray-300">
                                {uploadingImageIndex === idx ? "Uploading…" : "Replace"}
                              </span>
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                disabled={uploadingImageIndex !== null}
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  if (f) handleImageUpload(f, idx);
                                  e.target.value = "";
                                }}
                              />
                            </label>
                            <button
                              type="button"
                              onClick={() => removeContentUrl(idx)}
                              className="text-red-600 text-sm"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                        <label className="flex items-center gap-2">
                          <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-sm">
                            {uploadingImageIndex === -1 ? "Uploading…" : "Upload image"}
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            disabled={uploadingImageIndex !== null}
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) handleImageUpload(f, -1);
                              e.target.value = "";
                            }}
                          />
                        </label>
                      </div>
                    </div>
                  )}

                  {formData.section_type === "VIDEO" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Video URLs (one per line)</label>
                      <textarea
                        value={getContentVideoUrls().join("\n")}
                        onChange={(e) =>
                          setContentUrls(e.target.value.split("\n"))
                        }
                        rows={3}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                        placeholder="https://..."
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Background (hex)</label>
                    <input
                      type="text"
                      name="background"
                      value={formData.background}
                      onChange={handleInputChange}
                      placeholder="#FFFFFF"
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Display order</label>
                    <input
                      type="number"
                      name="display_order"
                      value={formData.display_order}
                      onChange={handleInputChange}
                      min={0}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        resetForm();
                      }}
                      className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                    >
                      {editingId ? "Update" : "Create"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ServiceContentSections;
